import { Component } from '@theme/component'
import '@theme/gsap'
import { clamp, closest } from '@theme/maths'

const UiSliderConfig = {
  baseDuration: 0.5,
  dragSlop: 6,
  durationPerPage: 0.28,
  ease: 'power4.out',
  inviewRatio: 0.5,
  maxPages: 2,
  pointerQuery: '(hover: hover) and (pointer: fine)',
  throwDecay: 0.16,
  velocityCap: 4200
}

const SliderTag = 'ui-slider'
const NavDirections = new Map([
  ['prev', 'prev'],
  ['next', 'next']
])
const NavIsDirections = new Map([
  ['previous-button', 'prev'],
  ['next-button', 'next']
])

/**
 * @typedef {object} SliderMetrics
 * @property {number} clientWidth - Visible width of the scroller.
 * @property {number} scrollWidth - Full scrollable width of the scroller.
 */

/** @param {Element} button @returns {'prev' | 'next' | null} */
function navDirectionOf(button) {
  const action = button.getAttribute('on:click')
  if (action) return NavDirections.get(action.split('/').pop()) ?? null
  return NavIsDirections.get(button.getAttribute('is') ?? '') ?? null
}

/** @param {NodeListOf<Element>} buttons @param {Element | null} owner @returns {HTMLButtonElement[]} */
function navButtonsOf(buttons, owner) {
  return Array.from(buttons).filter(
    (button) => button.closest(SliderTag) === owner && navDirectionOf(button)
  )
}

let navDelegationStarted = false

function startNavDelegation() {
  if (navDelegationStarted) return
  navDelegationStarted = true
  document.addEventListener('click', handleDelegatedNavClick)
}

/** @extends {Component} */
class UiSlider extends Component {
  requiredRefs = ['scroller']

  currentIndex = 0

  /** @type {SliderMetrics} */
  #metrics = { clientWidth: 0, scrollWidth: 0 }
  #snapPositions = [0]
  #dragMoved = false
  #dragOrigin = 0
  #dragPointer = null
  #dragStart = 0
  #dragTime = 0
  #dragVelocity = 0
  #dragX = 0
  #events = null
  #scrollTween = null
  #sizeObserver = null
  #syncFrame = null

  #killTween = () => {
    this.#scrollTween?.kill()
    this.#scrollTween = null
    const { scroller } = this.refs
    if (scroller) scroller.style.scrollSnapType = ''
  }

  #onScroll = () => {
    if (this.#syncFrame) return
    this.#syncFrame = requestAnimationFrame(() => {
      this.#syncFrame = null
      this.#sync()
    })
  }

  #onResize = () => {
    this.#measure()
    this.#sync()
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (!this.#canDrag(event)) return
    const { scroller } = this.refs

    this.#killTween()
    this.#dragPointer = event.pointerId
    this.#dragMoved = false
    this.#dragOrigin = event.clientX
    this.#dragStart = scroller.scrollLeft
    this.#dragTime = event.timeStamp
    this.#dragVelocity = 0
    this.#dragX = event.clientX
    scroller.style.scrollSnapType = 'none'
    this.dataset.drag = 'grabbing'
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (this.#dragPointer !== event.pointerId) return
    const { scroller } = this.refs

    const travel = event.clientX - this.#dragOrigin
    if (Math.abs(travel) > UiSliderConfig.dragSlop) this.#captureDrag(event)

    this.#trackVelocity(event)
    scroller.scrollLeft = this.#clampScroll(this.#dragStart - travel)
  }

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (this.#dragPointer !== event.pointerId) return
    this.#dragPointer = null
    delete this.dataset.drag

    const { scroller } = this.refs
    const thrown =
      scroller.scrollLeft + this.#dragVelocity * UiSliderConfig.throwDecay
    this.#tweenTo(closest(this.#snapPositions, this.#clampScroll(thrown)))
    requestAnimationFrame(this.#clearDragMoved)
  }

  #clearDragMoved = () => {
    this.#dragMoved = false
  }

  /** @param {Event} event */
  #onDragStart = (event) => {
    if (this.#dragPointer !== null) event.preventDefault()
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!this.#dragMoved) return
    event.preventDefault()
    event.stopPropagation()
  }

  connectedCallback() {
    super.connectedCallback()
    const { scroller } = this.refs
    if (!scroller) return

    startNavDelegation()
    this.#bindEvents(scroller)
    this.#sizeObserver = new ResizeObserver(this.#onResize)
    this.#sizeObserver.observe(scroller)
    this.#measure()
    this.#sync()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#events?.abort()
    this.#events = null
    this.#sizeObserver?.disconnect()
    this.#sizeObserver = null
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame)
    this.#syncFrame = null
    this.#killTween()
  }

  /** @param {Event} [event] */
  next(event) {
    event?.preventDefault?.()
    this.select(this.currentIndex + 1)
  }

  /** @param {Event} [event] */
  prev(event) {
    event?.preventDefault?.()
    this.select(this.currentIndex - 1)
  }

  /** @param {number} index */
  select(index) {
    const lastIndex = this.#snapPositions.length - 1
    if (lastIndex < 0) return

    this.#tweenTo(this.#snapPositions[clamp(0, Math.round(index), lastIndex)])
  }

  /** @param {HTMLElement} scroller */
  #bindEvents(scroller) {
    this.#events = new AbortController()
    const { signal } = this.#events
    const passive = { passive: true, signal }

    scroller.addEventListener('scroll', this.#onScroll, passive)
    scroller.addEventListener('wheel', this.#killTween, passive)
    scroller.addEventListener('dragstart', this.#onDragStart, { signal })
    scroller.addEventListener('pointerdown', this.#onPointerDown, { signal })
    scroller.addEventListener('pointermove', this.#onPointerMove, { signal })
    scroller.addEventListener('pointerup', this.#onPointerUp, { signal })
    scroller.addEventListener('pointercancel', this.#onPointerUp, { signal })
    scroller.addEventListener('click', this.#onClick, { capture: true, signal })
  }

  /** @returns {HTMLElement[]} */
  #slides() {
    const { scroller } = this.refs
    if (!scroller) return []
    const selector = this.getAttribute('selector')
    return Array.from(scroller.querySelectorAll(selector))
  }

  #measure() {
    const { scroller } = this.refs
    if (!scroller) return

    const { clientWidth, scrollWidth } = scroller
    this.#metrics = { clientWidth, scrollWidth }

    const maxScroll = Math.max(0, scrollWidth - clientWidth)
    const snapport =
      scroller.getBoundingClientRect().left + this.#scrollPaddingStart(scroller)
    const positions = this.#slides().map((slide) => {
      const offset = slide.getBoundingClientRect().left - snapport
      return clamp(0, Math.round(scroller.scrollLeft + offset), maxScroll)
    })

    this.#snapPositions = positions.length ? positions : [0]
  }

  /** @param {HTMLElement} scroller @returns {number} */
  #scrollPaddingStart(scroller) {
    const parsed = Number.parseFloat(
      getComputedStyle(scroller).scrollPaddingInlineStart
    )
    return Number.isFinite(parsed) ? parsed : 0
  }

  /** @param {number} position @returns {number} */
  #clampScroll(position) {
    const { clientWidth, scrollWidth } = this.#metrics
    return clamp(0, position, Math.max(0, scrollWidth - clientWidth))
  }

  /** @param {number} position */
  #tweenTo(position) {
    const { scroller } = this.refs
    if (!scroller) return

    const target = this.#clampScroll(position)
    this.#killTween()

    const gsap = window.gsap
    if (!gsap) {
      scroller.scrollTo({ behavior: 'smooth', left: target })
      return
    }

    scroller.style.scrollSnapType = 'none'
    this.#scrollTween = gsap.to(scroller, {
      duration: this.#tweenDuration(scroller.scrollLeft, target),
      ease: UiSliderConfig.ease,
      onComplete: this.#killTween,
      onInterrupt: this.#killTween,
      overwrite: true,
      scrollLeft: target
    })
  }

  /** @param {number} from @param {number} to @returns {number} */
  #tweenDuration(from, to) {
    const width = this.#metrics.clientWidth || 1
    const pages = Math.min(Math.abs(to - from) / width, UiSliderConfig.maxPages)
    return UiSliderConfig.baseDuration + pages * UiSliderConfig.durationPerPage
  }

  /** @param {PointerEvent} event @returns {boolean} */
  #canDrag(event) {
    if (event.button !== 0 || this.#dragPointer !== null) return false
    if (event.pointerType !== 'mouse') return false
    if (!window.matchMedia?.(UiSliderConfig.pointerQuery).matches) return false
    return this.#metrics.scrollWidth > this.#metrics.clientWidth + 1
  }

  /** @param {PointerEvent} event */
  #captureDrag(event) {
    if (this.#dragMoved) return

    this.#dragMoved = true
    this.refs.scroller?.setPointerCapture?.(event.pointerId)
  }

  /** @param {PointerEvent} event */
  #trackVelocity(event) {
    const elapsed = event.timeStamp - this.#dragTime
    if (elapsed <= 0) return

    const perSecond = ((event.clientX - this.#dragX) / elapsed) * -1000
    this.#dragVelocity = clamp(
      -UiSliderConfig.velocityCap,
      perSecond,
      UiSliderConfig.velocityCap
    )
    this.#dragTime = event.timeStamp
    this.#dragX = event.clientX
  }

  #sync() {
    const { scroller } = this.refs
    if (!scroller) return

    const { clientWidth, scrollWidth } = this.#metrics
    const scrollLeft = scroller.scrollLeft
    const atStart = scrollLeft <= 1
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1

    this.currentIndex = this.#snapPositions.indexOf(
      closest(this.#snapPositions, scrollLeft)
    )

    this.#syncSlides(scroller)
    this.#syncNavButtons(atStart, atEnd)
    this.#syncProgress(scrollLeft)
  }

  /** @param {HTMLElement} scroller */
  #syncSlides(scroller) {
    const viewport = scroller.getBoundingClientRect()
    this.#slides().forEach((slide, index) => {
      const rect = slide.getBoundingClientRect()
      const overlap =
        Math.min(rect.right, viewport.right) -
        Math.max(rect.left, viewport.left)
      const inview = overlap >= rect.width * UiSliderConfig.inviewRatio

      slide.dataset.slideStatus = this.#slideStatus(index, inview)
    })
  }

  /** @param {number} index @param {boolean} inview @returns {string} */
  #slideStatus(index, inview) {
    if (index === this.currentIndex) return 'active'
    return inview ? 'inview' : 'not-active'
  }

  /** @param {boolean} atStart @param {boolean} atEnd */
  #syncNavButtons(atStart, atEnd) {
    const scrollable = this.#metrics.scrollWidth > this.#metrics.clientWidth + 1
    for (const button of this.#navButtons()) {
      const goesBack = navDirectionOf(button) === 'prev'
      button.disabled = !scrollable || (goesBack ? atStart : atEnd)
    }
  }

  /** @returns {HTMLButtonElement[]} */
  #navButtons() {
    const owned = navButtonsOf(this.querySelectorAll('button'), this)
    if (!this.id) return owned

    const external = navButtonsOf(
      document.querySelectorAll(`button[aria-controls="${this.id}"]`),
      null
    )
    return [...new Set([...owned, ...external])]
  }

  /** @param {number} scrollLeft */
  #syncProgress(scrollLeft) {
    const { progressIndicator } = this.refs
    if (!(progressIndicator instanceof HTMLElement)) return

    const { clientWidth, scrollWidth } = this.#metrics
    const maxScroll = scrollWidth - clientWidth
    if (maxScroll <= 0) {
      progressIndicator.style.width = '100%'
      return
    }

    const viewRatio = clientWidth / scrollWidth
    const progress = viewRatio + (scrollLeft / maxScroll) * (1 - viewRatio)
    progressIndicator.style.width = `${(progress * 100).toFixed(2)}%`
  }
}

/** @param {MouseEvent} event */
function handleDelegatedNavClick(event) {
  const button = event.target?.closest?.('button[aria-controls]')
  if (!button || button.hasAttribute('on:click')) return

  const direction = navDirectionOf(button)
  if (!direction) return

  const slider = document.getElementById(button.getAttribute('aria-controls'))
  if (!(slider instanceof UiSlider)) return

  if (direction === 'prev') slider.prev(event)
  else slider.next(event)
}

if (!customElements.get('ui-slider')) {
  customElements.define('ui-slider', UiSlider)
}
